import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import { CallToolRequestSchema, ListToolsRequestSchema } from '@modelcontextprotocol/sdk/types.js';
import { promises as fs } from 'fs';
import path from 'path';
import { getTools } from './get-tools.js';

const server = new Server(
  {
    name: '@workos/mcp-docs-server',
    version: '0.1.0',
  },
  {
    capabilities: {
      tools: {},
    },
  },
);

// List available tools
server.setRequestHandler(ListToolsRequestSchema, async () => {
  const tools = await getTools();

  return {
    tools: [
      {
        name: 'workos_docs',
        description:
          "Get WorkOS SDK documentation content by path. Use this when you know the documentation structure or want to browse hierarchically. If you don't know the exact path, use workos_search first to find relevant documents. Returns the requested doc content plus a list of other docs in the same directory to help with navigation.",
        inputSchema: {
          type: 'object',
          properties: {
            path: {
              type: 'string',
              description:
                'Documentation path to retrieve. Use forward slashes to navigate folders. Examples: "sso/overview", "directory-sync/introduction", "user-management/authentication". Try broad categories first (e.g., "sso", "directory-sync") to see available sub-pages. Omit file extensions (.md, .mdx).',
            },
          },
          required: ['path'],
        },
      },
      {
        name: 'workos_search',
        description:
          "Search through all WorkOS documentation for specific keywords or phrases. Use this when you need to find information but don't know the exact documentation path. Returns snippets of matching content with their file paths.",
        inputSchema: {
          type: 'object',
          properties: {
            query: {
              type: 'string',
              description:
                'Search terms to look for in the documentation. Can be keywords, method names, error messages, or phrases. Examples: "SSO authentication", "directory sync webhook", "MagicAuth", "SAML metadata".',
            },
            maxResults: {
              type: 'number',
              description: 'Maximum number of results to return. Defaults to 10.',
              default: 10,
            },
          },
          required: ['query'],
        },
      },
      {
        name: 'workos_examples',
        description:
          'Get WorkOS SDK code examples. Use this after checking documentation to find practical implementation examples. Returns code examples grouped by feature or use case.',
        inputSchema: {
          type: 'object',
          properties: {
            example: {
              type: 'string',
              description:
                'Example name or group to retrieve. Common groups: "sso", "directory-sync", "user-management", "audit-logs", "webhooks". Start with the feature name to see all related examples.',
            },
          },
          required: ['example'],
        },
      },
      {
        name: 'workos_changelogs',
        description:
          'Get WorkOS package changelogs to check version history, breaking changes, and new features. IMPORTANT: For general WorkOS platform updates, new features, or "what\'s new" questions, always use the exact package name "workos-platform" (not "workos"). Use SDK-specific packages like "@workos/node" only for SDK version compatibility and migration guides.',
        inputSchema: {
          type: 'object',
          properties: {
            package: {
              type: 'string',
              description:
                'Package name to get changelog for. MUST use exact string "workos-platform" (not "workos") for general platform updates and new features from RSS feed. Use "@workos/node", "@workos/python", "@workos/ruby", "@workos/go", "@workos/php" for SDK-specific changelogs.',
            },
          },
          required: ['package'],
        },
      },
    ],
  };
});

// Handle tool calls
server.setRequestHandler(CallToolRequestSchema, async (request) => {
  const { name, arguments: args } = request.params;
  const tools = await getTools();

  try {
    switch (name) {
      case 'workos_docs': {
        const docPath = args?.path as string;
        if (!docPath) {
          throw new Error('Path parameter is required for workos_docs');
        }

        // Try to read from local docs first
        if (tools.docs.docsPath.startsWith('http')) {
          // Remote fallback - would need HTTP client in real implementation
          return {
            content: [
              {
                type: 'text',
                text: `Documentation for ${docPath} would be fetched from ${tools.docs.docsPath}`,
              },
            ],
          };
        } else {
          // Read from local file system
          const tryPaths = [
            path.join(tools.docs.docsPath, `${docPath}.md`),
            path.join(tools.docs.docsPath, `${docPath}.mdx`),
            path.join(tools.docs.docsPath, `${docPath}/index.md`),
            path.join(tools.docs.docsPath, `${docPath}/index.mdx`),
          ];

          for (const tryPath of tryPaths) {
            try {
              const content = await fs.readFile(tryPath, 'utf-8');

              // Get the directory of the found file
              const fileDir = path.dirname(tryPath);
              const parentDir = path.dirname(fileDir);

              // Get other docs in the same directory
              const glob = await import('fast-glob');
              const siblingDocs = await glob.default(`${fileDir}/*.{md,mdx}`);
              const parentDocs = await glob.default(`${parentDir}/*.{md,mdx}`);

              // Format sibling paths relative to docs root
              const siblingPaths = siblingDocs
                .map((p) => path.relative(tools.docs.docsPath, p).replace(/\.(mdx?|md)$/, ''))
                .filter((p) => !p.endsWith('/index')) // Remove index files
                .sort();

              const parentPaths = parentDocs
                .map((p) => path.relative(tools.docs.docsPath, p).replace(/\.(mdx?|md)$/, ''))
                .filter((p) => !p.endsWith('/index'))
                .sort();

              // Get subdirectories
              const subdirs = await glob.default(`${fileDir}/*/`, { onlyDirectories: true });
              const subdirPaths = subdirs.map((p) => path.relative(tools.docs.docsPath, p).replace(/\/$/, '')).sort();

              let navigationHint = '\n\n---\n### Navigation Hints\n';

              if (subdirPaths.length > 0) {
                navigationHint += `\n**Subdirectories in ${path.relative(tools.docs.docsPath, fileDir)}:**\n`;
                navigationHint += subdirPaths.map((p) => `- ${p}/`).join('\n');
              }

              if (siblingPaths.length > 0) {
                navigationHint += `\n\n**Other pages in ${path.relative(tools.docs.docsPath, fileDir)}:**\n`;
                navigationHint += siblingPaths.map((p) => `- ${p}`).join('\n');
              }

              if (parentPaths.length > 0 && fileDir !== tools.docs.docsPath) {
                navigationHint += `\n\n**Parent directory pages:**\n`;
                navigationHint += parentPaths.map((p) => `- ${p}`).join('\n');
              }

              navigationHint += '\n\n💡 **Tip:** Use these paths with workos_docs to explore related documentation.';

              return {
                content: [
                  {
                    type: 'text',
                    text: content + navigationHint,
                  },
                ],
              };
            } catch {
              // Continue to next path
            }
          }

          // File not found, suggest similar paths
          const glob = await import('fast-glob');
          const allDocs = await glob.default(`${tools.docs.docsPath}/**/*.{md,mdx}`);
          const relativePaths = allDocs.map((p) => path.relative(tools.docs.docsPath, p).replace(/\.(mdx?|md)$/, ''));

          // Find similar paths
          const searchTerm = docPath.toLowerCase();
          const searchParts = searchTerm.split('/');
          const lastPart = searchParts[searchParts.length - 1];

          // Try to find docs that match any part of the search term
          const similarPaths = relativePaths
            .filter((p) => {
              const pLower = p.toLowerCase();
              return (
                pLower.includes(searchTerm) ||
                searchTerm.includes(pLower.split('/').pop() || '') ||
                pLower.includes(lastPart)
              );
            })
            .slice(0, 10);

          // Get top-level directories
          const topLevelDirs = [...new Set(relativePaths.map((p) => p.split('/')[0]))]
            .filter((d) => d && !d.includes('.'))
            .sort();

          // Try to find the parent directory if the path contains a slash
          let parentDirSuggestions = '';
          if (searchParts.length > 1) {
            const parentPath = searchParts.slice(0, -1).join('/');
            const docsInParent = relativePaths
              .filter((p) => p.startsWith(parentPath + '/'))
              .map((p) => p.replace(parentPath + '/', '').split('/')[0])
              .filter((v, i, a) => a.indexOf(v) === i) // unique
              .slice(0, 10);

            if (docsInParent.length > 0) {
              parentDirSuggestions = `\n\n**Available pages in "${parentPath}":**\n${docsInParent.map((p) => `- ${parentPath}/${p}`).join('\n')}`;
            }
          }

          let errorMessage = `📚 Documentation path "${docPath}" not found.\n\n`;

          if (similarPaths.length > 0) {
            errorMessage += `**Similar documentation pages:**\n${similarPaths.map((p) => `- ${p}`).join('\n')}`;
          }

          errorMessage += parentDirSuggestions;

          errorMessage += `\n\n**Top-level documentation categories:**\n${topLevelDirs.map((d) => `- ${d}/`).join('\n')}`;

          errorMessage += `\n\n💡 **Tips for exploring WorkOS docs:**
- Start with a top-level category (e.g., "sso", "directory-sync")
- Browse subdirectories to find specific topics
- Common paths include:
  - sso/overview
  - directory-sync/introduction
  - user-management/authentication
  - audit-logs/overview
  - webhooks/overview`;

          throw new Error(errorMessage);
        }
      }

      case 'workos_search': {
        const query = args?.query as string;
        const maxResults = (args?.maxResults as number) || 10;

        if (!query) {
          throw new Error('Query parameter is required for workos_search');
        }

        if (tools.docs.docsPath.startsWith('http')) {
          return {
            content: [
              {
                type: 'text',
                text: `Search functionality for remote documentation is not yet implemented. Would search for "${query}" in ${tools.docs.docsPath}`,
              },
            ],
          };
        } else {
          const glob = await import('fast-glob');

          try {
            // Get all documentation files
            const allDocs = await glob.default(`${tools.docs.docsPath}/**/*.{md,mdx}`);

            // Search for the query in all files
            const searchResults: Array<{
              file: string;
              matches: Array<{
                lineNumber: number;
                line: string;
                context: string[];
              }>;
            }> = [];

            for (const docFile of allDocs) {
              try {
                const relativePath = path.relative(tools.docs.docsPath, docFile).replace(/\.(mdx?|md)$/, '');
                const matches: Array<{
                  lineNumber: number;
                  line: string;
                  context: string[];
                }> = [];

                // Use Node.js file reading to search
                const content = await fs.readFile(docFile, 'utf-8');
                const lines = content.split('\n');
                const queryLower = query.toLowerCase();

                for (let i = 0; i < lines.length; i++) {
                  if (lines[i].toLowerCase().includes(queryLower)) {
                    const context: string[] = [];
                    // Get 2 lines before
                    for (let j = Math.max(0, i - 2); j < i; j++) {
                      context.push(lines[j]);
                    }
                    // Get 2 lines after
                    const afterContext: string[] = [];
                    for (let j = i + 1; j < Math.min(lines.length, i + 3); j++) {
                      afterContext.push(lines[j]);
                    }

                    matches.push({
                      lineNumber: i + 1,
                      line: lines[i],
                      context: [...context, ...afterContext],
                    });

                    // Limit matches per file
                    if (matches.length >= 3) break;
                  }
                }

                if (matches.length > 0) {
                  searchResults.push({
                    file: relativePath,
                    matches: matches,
                  });
                }
              } catch (error) {
                // Log error for debugging but continue with other files
                console.error(`Error searching in ${docFile}:`, error);
              }
            }

            // Sort results by number of matches (relevance)
            searchResults.sort((a, b) => b.matches.length - a.matches.length);
            const topResults = searchResults.slice(0, maxResults);

            if (topResults.length === 0) {
              throw new Error(`No results found for "${query}"`);
            }

            // Format results
            let result = `# Search Results for "${query}"\n\n`;
            result += `Found ${searchResults.length} document(s) containing matches.\n\n`;

            for (const docResult of topResults) {
              result += `## 📄 ${docResult.file}\n\n`;

              for (const match of docResult.matches) {
                result += `**Line ${match.lineNumber}:**\n`;
                result += '```\n';
                if (match.context.length > 0) {
                  result += match.context.join('\n') + '\n';
                }
                result += `>>> ${match.line}\n`;
                result += '```\n\n';
              }

              result += `➡️ Use \`workos_docs\` with path "${docResult.file}" to see the full document.\n\n`;
              result += '---\n\n';
            }

            // Add search tips
            result += `\n💡 **Search Tips:**
- Use specific terms or method names for better results
- Try different variations of your search terms
- Use the document paths shown above with workos_docs to read full context
- Combine multiple search terms for more specific results`;

            return {
              content: [
                {
                  type: 'text',
                  text: result,
                },
              ],
            };
          } catch (error) {
            if (error instanceof Error && error.message.includes('No results found')) {
              // Provide helpful suggestions when no results found
              let errorMessage = `🔍 No results found for "${query}".\n\n`;

              // Suggest breaking down the query
              const words = query.split(/\s+/).filter((w) => w.length > 2);
              if (words.length > 1) {
                errorMessage += `**Try searching for individual terms:**\n`;
                errorMessage += words.map((w) => `- "${w}"`).join('\n');
                errorMessage += '\n\n';
              }

              errorMessage += `**Search suggestions:**
- Use more specific technical terms (e.g., "SAML", "OAuth", "webhook")
- Try searching for method or class names
- Search for error messages or status codes
- Use feature names like "SSO", "Directory Sync", "MagicAuth"

**Alternative approaches:**
- Use \`workos_docs\` to browse documentation by category
- Start with top-level paths like "sso", "directory-sync", "user-management"`;

              throw new Error(errorMessage);
            }
            throw error;
          }
        }
      }

      case 'workos_examples': {
        const example = args?.example as string;
        if (!example) {
          throw new Error('Example parameter is required for workos_examples');
        }

        if (tools.examples.examplesPath.startsWith('http')) {
          return {
            content: [
              {
                type: 'text',
                text: `Example ${example} would be fetched from ${tools.examples.examplesPath}`,
              },
            ],
          };
        } else {
          // Read example files from local file system
          const exampleDir = path.join(tools.examples.examplesPath, example);
          const indexPath = path.join(exampleDir, 'index.json');

          try {
            const indexContent = await fs.readFile(indexPath, 'utf-8');
            const exampleGroup = JSON.parse(indexContent);

            let result = `# ${exampleGroup.name} Examples\n\n`;

            for (const file of exampleGroup.files) {
              const filePath = path.join(tools.examples.examplesPath, file);
              const fileContent = await fs.readFile(filePath, 'utf-8');
              const fileName = path.basename(file);

              result += `## ${fileName}\n\n\`\`\`typescript\n${fileContent}\n\`\`\`\n\n`;
            }

            // Add navigation hints for other example groups
            const glob = await import('fast-glob');
            const allExampleDirs = await glob.default(`${tools.examples.examplesPath}/*/index.json`);
            const otherExamples = allExampleDirs
              .map((p) => path.basename(path.dirname(p)))
              .filter((e) => e !== example)
              .sort();

            if (otherExamples.length > 0) {
              result += `\n---\n### Other Example Groups\n`;
              result += otherExamples.map((e) => `- ${e}`).join('\n');
              result += '\n\n💡 **Tip:** Use these names with workos_examples to see other code examples.';
            }

            return {
              content: [
                {
                  type: 'text',
                  text: result,
                },
              ],
            };
          } catch {
            // Get available example groups
            const glob = await import('fast-glob');
            const allExampleDirs = await glob.default(`${tools.examples.examplesPath}/*/index.json`);
            const availableExamples = allExampleDirs.map((p) => path.basename(path.dirname(p))).sort();

            let errorMessage = `📝 Example group "${example}" not found.\n\n`;

            if (availableExamples.length > 0) {
              errorMessage += `**Available example groups:**\n${availableExamples.map((e) => `- ${e}`).join('\n')}`;

              // Find similar examples
              const similar = availableExamples.filter(
                (e) =>
                  e.toLowerCase().includes(example.toLowerCase()) || example.toLowerCase().includes(e.toLowerCase()),
              );

              if (similar.length > 0 && similar.length < availableExamples.length) {
                errorMessage += `\n\n**Did you mean:**\n${similar.map((e) => `- ${e}`).join('\n')}`;
              }
            }

            errorMessage += `\n\n💡 **Tips for using examples:**
- Check workos_docs first to understand the concept
- Use example group names that match features (e.g., "sso", "directory-sync")
- Examples show practical implementations with best practices`;

            throw new Error(errorMessage);
          }
        }
      }

      case 'workos_changelogs': {
        const packageName = args?.package as string;
        if (!packageName) {
          throw new Error('Package parameter is required for workos_changelogs');
        }

        if (tools.changelogs.changelogsPath.startsWith('http')) {
          return {
            content: [
              {
                type: 'text',
                text: `Changelog for ${packageName} would be fetched from ${tools.changelogs.changelogsPath}`,
              },
            ],
          };
        } else {
          const changelogPath = path.join(tools.changelogs.changelogsPath, `${packageName}.json`);

          try {
            const content = await fs.readFile(changelogPath, 'utf-8');
            const changelog = JSON.parse(content);

            let result = `# ${packageName} Changelog\n\n`;

            for (const entry of changelog) {
              if (entry.source === 'rss') {
                // RSS entries use the title as the version/feature name
                result += `## ${entry.version}\n`;
                result += `**Date:** ${entry.date}\n`;
                if (entry.url) {
                  result += `**Link:** ${entry.url}\n`;
                }
                result += '\n';
                for (const change of entry.changes) {
                  result += `${change}\n`;
                }
                result += '\n---\n\n';
              } else {
                // Traditional markdown changelog format
                result += `## [${entry.version}] - ${entry.date}\n\n`;
                for (const change of entry.changes) {
                  result += `- ${change}\n`;
                }
                result += '\n';
              }
            }

            // Add list of other available changelogs
            const glob = await import('fast-glob');
            const allChangelogs = await glob.default(`${tools.changelogs.changelogsPath}/*.json`);
            const otherPackages = allChangelogs
              .map((p) => path.basename(p, '.json'))
              .filter((p) => p !== packageName)
              .sort();

            if (otherPackages.length > 0) {
              result += `\n---\n### Other Package Changelogs\n`;
              result += otherPackages.map((p) => `- ${p}`).join('\n');
              result += '\n\n💡 **Tip:** Use these package names with workos_changelogs to see their version history.';
            }

            return {
              content: [
                {
                  type: 'text',
                  text: result,
                },
              ],
            };
          } catch {
            // Get available changelogs
            const glob = await import('fast-glob');
            const allChangelogs = await glob.default(`${tools.changelogs.changelogsPath}/*.json`);
            const availablePackages = allChangelogs.map((p) => path.basename(p, '.json')).sort();

            let errorMessage = `📋 Changelog for package "${packageName}" not found.\n\n`;

            if (availablePackages.length > 0) {
              errorMessage += `**Available package changelogs:**\n${availablePackages.map((p) => `- ${p}`).join('\n')}`;

              // Find similar packages
              const similar = availablePackages.filter(
                (p) =>
                  p.toLowerCase().includes(packageName.toLowerCase()) ||
                  packageName.toLowerCase().includes(p.toLowerCase()),
              );

              if (similar.length > 0 && similar.length < availablePackages.length) {
                errorMessage += `\n\n**Did you mean:**\n${similar.map((p) => `- ${p}`).join('\n')}`;
              }
            }

            errorMessage += `\n\n💡 **Choose the right changelog:**
- **workos-platform** - For product announcements, new features, service updates, or "what's new" questions
- **@workos/node** - For Node.js SDK version history, migration guides, and troubleshooting
- **@workos/python** - For Python SDK version history and compatibility
- **@workos/ruby** - For Ruby SDK version history and compatibility
- **@workos/go** - For Go SDK version history and compatibility
- **@workos/php** - For PHP SDK version history and compatibility
- **@workos/kotlin** - For Kotlin SDK version history and compatibility

🤖 **AI Assistant tip**: If the user asks about "what's new", "recent updates", or "new features", use workos-platform. If they mention a specific SDK, version number, or technical issue, use the relevant SDK package.`;

            throw new Error(errorMessage);
          }
        }
      }

      default:
        throw new Error(`Unknown tool: ${name}`);
    }
  } catch (error) {
    return {
      content: [
        {
          type: 'text',
          text: `Error: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
      isError: true,
    };
  }
});

async function main() {
  const transport = new StdioServerTransport();
  await server.connect(transport);
  console.error('WorkOS MCP Documentation Server running on stdio');
}

// Export getTools for programmatic usage
export { getTools } from './get-tools.js';

main().catch((error) => {
  console.error('Fatal error in main():', error);
  process.exit(1);
});
